// Cycle list at /portal/health: every assessment cycle in the caller's scope.
// A facilitator sees their assigned cycles, a church leader their church's,
// the Registrar all of them.

import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { useHealthFrame } from './HealthLayout';
// Admin settings are reached via the "Settings" sub-nav tab (HealthLayout);
// this page deliberately carries no duplicate link to them.
import { CYCLE_STATUS_LABELS, formatNzDateShort } from './format';
import type { CycleSummary } from './api';

function statusPillClass(status: string): string {
  switch (status) {
    case 'open':
      return 'pill pill-success';
    case 'closed':
      return 'pill pill-info';
    case 'debriefed':
      return 'pill pill-warn';
    default:
      return 'pill pill-neutral';
  }
}

export function CycleListPage(): JSX.Element {
  const { role, cycles } = useHealthFrame();

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <div>
          <h1 style={{ fontSize: 22, marginBottom: 4 }}>Church Health Assessments</h1>
          <p style={{ fontSize: 13.5, color: 'var(--health-grey)', marginBottom: 18 }}>
            An invitation to honest reflection on the life of each church, taken together by its
            leaders.
          </p>
        </div>
        {role === 'admin' && (
          <Link to="/portal/health/new" className="btn btn-primary btn-sm">
            New assessment
          </Link>
        )}
      </div>

      {cycles.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '40px 24px' }}>
          <p style={{ fontSize: 14.5, marginBottom: 6 }}>No assessments here yet.</p>
          <p style={{ fontSize: 13.5, color: 'var(--health-grey)', lineHeight: 1.7 }}>
            {role === 'admin'
              ? 'Launch one with "New assessment" above, or open a cycle from the CRM — either way it appears here, ready for invitations to go out.'
              : 'When a church opens an assessment cycle it will appear on this page, ready for invitations to go out.'}
          </p>
        </div>
      ) : (
        <div className="card" style={{ padding: '8px 16px 4px' }}>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Church</th>
                  <th scope="col">Status</th>
                  <th scope="col">Window</th>
                  <th scope="col">Facilitator</th>
                  <th scope="col">Invited</th>
                  <th scope="col">Submitted</th>
                  <th scope="col" aria-label="Open" />
                </tr>
              </thead>
              <tbody>
                {cycles.map((cycle) => (
                  <CycleRow key={cycle.id} cycle={cycle} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function CycleRow({ cycle }: { cycle: CycleSummary }): JSX.Element {
  const window =
    [formatNzDateShort(cycle.opens_at), formatNzDateShort(cycle.closes_at)]
      .filter(Boolean)
      .join(' to ') || 'Not set';
  return (
    <tr>
      <td style={{ fontWeight: 600 }}>
        <Link to={`/portal/health/${cycle.id}`}>{cycle.church_name}</Link>
        {cycle.mode === 'open' && (
          <span className="pill pill-neutral" style={{ marginLeft: 8 }}>
            Open link
          </span>
        )}
      </td>
      <td>
        <span className={statusPillClass(cycle.status)}>
          {CYCLE_STATUS_LABELS[cycle.status] ?? cycle.status}
        </span>
      </td>
      <td style={{ whiteSpace: 'nowrap' }}>{window}</td>
      <td>{cycle.facilitator_name ?? ''}</td>
      <td>{cycle.invited}</td>
      <td>{cycle.submitted}</td>
      <td>
        <Link to={`/portal/health/${cycle.id}`} className="btn btn-ghost btn-sm">
          View
        </Link>
      </td>
    </tr>
  );
}
